import { ChatInput } from './ChatInput';
import { ChatMessageList } from './ChatMessageList';
import { HeaderChat } from './HeaderChat';
import { useChatMessages } from '@/hooks';
import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { CaseLabel } from '../inbox';
import AppRoutes from '@/const/routes';
import Link from 'next/link';
import { Icon, Spinner } from '@/components/common';
import Icons from '@/const/icons';
import { useSearchParams } from 'next/navigation';

interface ChatBoxProps {
	toggle?: () => void;
	close: () => void;
	className?: string;
	from?: string;
}

export const ChatBoxContainer: React.FC<ChatBoxProps> = ({
	toggle,
	close,
	className,
	from,
}) => {
	const searchParams = useSearchParams();
	const page = searchParams.get('page');
	const containerRef = useRef<any>(null);
	const {
		chatMessages,
		isConversationOver,
		isLoading,
		addHelloMessage,
		streamingMessageId,
		currentBotMessage,
		// threadId,
		isChatAI,
		conversationInfo,
		isLoadingConversation,
	} = useChatMessages();

	const [showSpinner, setShowSpinner] = useState(true);

	useEffect(() => {
		if (isLoadingConversation && isChatAI) {
			setShowSpinner(true);
		} else {
			const timer = setTimeout(() => {
				setShowSpinner(false);
			}, 2000);
			return () => clearTimeout(timer);
		}
	}, [isLoadingConversation]);

	useEffect(() => {
		if (chatMessages.length === 0) {
			addHelloMessage();
		}
	}, [chatMessages]);

	useEffect(() => {
		const container = containerRef.current;
		const scrollToBottom = (): void => {
			if (container) {
				requestAnimationFrame(() => {
					container.scrollTop = container.scrollHeight;
				});
			}
		};
		scrollToBottom();
	}, [chatMessages, isLoading, streamingMessageId, currentBotMessage]);

	if (showSpinner && isChatAI) {
		return (
			<div className="flex h-full w-full !min-h-fit md:!h-[70vh] 3xl:!h[75vh] items-center justify-center bg-grey-2 rounded-2xl">
				<Spinner type="loadingPage" />
			</div>
		);
	}

	return (
		<RafikiBgImage chatMessagesLength={isChatAI ? 0 : chatMessages.length}>
			{/* if is chatAI, we don't want to show the rafiki image */}
			{isChatAI && (
				<Link
					href={`${AppRoutes.CLIENTAPP_INBOX}?page=${page}`}
					type="button"
					className="mb-2"
				>
					<Icon
						src={Icons.arrowLeft}
						className="w-5 h-5 text-[#292D32]"
						width={20}
					/>
				</Link>
			)}
			<HeaderChat
				toggle={toggle}
				close={isChatAI ? undefined : close}
				from={from}
				chatMessages={chatMessages}
			/>
			{isChatAI && (
				<CaseLabel
					caseLabel={conversationInfo?.data.case.title ?? ''}
					category={conversationInfo?.data.case.category.name}
					className="mt-2"
				/>
			)}
			<ChatMessageList
				className={className}
				messages={chatMessages}
				containerRef={containerRef}
				streamingMessageId={streamingMessageId}
				currentBotMessage={currentBotMessage}
				isLoading={isLoading}
				applicationId={conversationInfo?.data?.applicationId}
				conversationId={conversationInfo?.data?.id}
			/>
			{!isConversationOver && <ChatInput />}
		</RafikiBgImage>
	);
};

interface RafikiBgImageProps {
	children?: React.ReactNode;
	chatMessagesLength: number;
}

const RafikiBgImage = ({
	children,
	chatMessagesLength,
}: RafikiBgImageProps): JSX.Element => {
	return (
		<div className="relative w-full h-full flex flex-col justify-between overflow-hidden">
			{chatMessagesLength === 1 && (
				<Image
					src="/img/dashboard/rafiki.webp"
					alt="rafiki background"
					width={200}
					height={180}
					className="absolute top-[13rem] left-1/2 -translate-x-1/2 object-contain pointer-events-none"
				/>
			)}
			{children}
		</div>
	);
};

export default RafikiBgImage;
